import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@common/components/icon';
import Spinner from '@common/components/spinner';
import ProgressBar from '@common/components/progress-bar';
import { combineClassNames } from '@utils/combineClassNames';
import { ETrialResearchStatus, type TrialResearchStatus } from '../../types';

type FindingState = 'done' | 'running' | 'pending';

type Finding = { key: string; state: FindingState; detail?: string };

/**
 * What the visitor sees while research runs: each source flips to done as
 * soon as the partial preview carries its data, so the wait reads as progress.
 */
type Translate = (key: string, opts?: Record<string, unknown>) => string;

export const deriveFindings = (research: TrialResearchStatus | null, t: Translate): Finding[] => {
  const preview = research?.preview;
  const finished = research?.status === ETrialResearchStatus.DONE || research?.status === ETrialResearchStatus.PARTIAL;
  const hasWebsite = !!preview?.name;
  const hasBrand = !!(preview?.logo_url || preview?.colors);
  const locations = preview?.locations_total_found ?? 0;

  const state = (done: boolean, runningAfter: boolean): FindingState => {
    if (done) return 'done';

    return runningAfter ? 'running' : 'pending';
  };

  return [
    { key: 'website', state: state(hasWebsite, true) },
    { key: 'brand', state: state(hasBrand, hasWebsite) },
    {
      key: 'locations',
      state: state(finished || locations > 0, hasBrand),
      detail: locations > 0 ? t('authentication:trial_preview_locations_found', { count: locations }) : undefined,
    },
    { key: 'news', state: state(finished, locations > 0) },
    { key: 'industry', state: state(finished, false) },
  ];
};

type Props = {
  domain: string;
  research: TrialResearchStatus | null;
};

export const ResearchPanel = ({ domain, research }: Props) => {
  const { t } = useTranslation();
  const findings = deriveFindings(research, t);
  const done = findings.filter((finding) => finding.state === 'done').length;
  const percent = Math.round((done / findings.length) * 90) + 5;

  return (
    <div className="FreeTrial__Card FreeTrial__Research">
      <div className="FreeTrial__Research__Header">
        <div className="FreeTrial__Research__Icon"><Icon type="public" /></div>
        <div>
          <h5>{t('authentication:trial_researching_card_title', { domain })}</h5>
          <div className="FreeTrial__Hint">{t('authentication:trial_researching_card_hint')}</div>
        </div>
      </div>
      <ProgressBar status={percent} processing />
      <ul className="FreeTrial__Findings">
        {findings.map((finding) => (
          <li
            key={finding.key}
            className={combineClassNames('FreeTrial__Finding', `FreeTrial__Finding--${finding.state}`)}
          >
            <span className="FreeTrial__Finding__Status">
              {finding.state === 'done' && <Icon type="check" size="small" />}
              {finding.state === 'running' && <Spinner />}
            </span>
            <span>
              <div className="FreeTrial__Finding__Label">{t(`authentication:trial_finding_${finding.key}`)}</div>
              {finding.detail && <div className="FreeTrial__Hint">{finding.detail}</div>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
